import React, { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Loader2, CheckCircle2, XCircle, Clock, Mail, Search, AlertTriangle } from 'lucide-react';
import { Input } from '@/components/ui/input';

const REASON_LABELS = {
  invalid_email: { label: 'Invalid email', color: 'text-rose-700 bg-rose-50 border-rose-300' },
  bounced:       { label: 'Bounced', color: 'text-rose-700 bg-rose-50 border-rose-300' },
  unsubscribed:  { label: 'Unsubscribed', color: 'text-amber-700 bg-amber-50 border-amber-300' },
  rate_limited:  { label: 'Rate limited', color: 'text-amber-700 bg-amber-50 border-amber-300' },
  missing_email: { label: 'Missing email', color: 'text-slate-700 bg-slate-100 border-slate-300' },
  smtp_error:    { label: 'SMTP error', color: 'text-rose-700 bg-rose-50 border-rose-300' },
  unknown:       { label: 'Unknown', color: 'text-slate-700 bg-slate-100 border-slate-300' },
};

const NewsletterDeliveryReportDialog = ({ open, onOpenChange, newsletterId, newsletterSubject }) => {
  const { api } = useAuth();
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);
  const [q, setQ] = useState('');
  const [tab, setTab] = useState('failed');

  useEffect(() => {
    if (!open || !newsletterId) return;
    setLoading(true);
    api.get(`/admin/newsletters/${newsletterId}/delivery-report`)
      .then((r) => setReport(r.data))
      .catch(() => setReport(null))
      .finally(() => setLoading(false));
  }, [open, newsletterId, api]);

  const filtered = (report?.deliveries || [])
    .filter((d) => tab === 'all' || d.status === tab)
    .filter((d) => !q.trim() || (d.email || '').toLowerCase().includes(q.trim().toLowerCase()));

  const summary = report?.summary || { sent: 0, failed: 0, pending: 0 };
  const total = summary.sent + summary.failed + summary.pending;
  const successRate = total ? Math.round((summary.sent / total) * 100) : 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="delivery-report-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Mail className="w-5 h-5 text-teal-500" /> Delivery report</DialogTitle>
          <DialogDescription className="line-clamp-1">{newsletterSubject}</DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-16 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-teal-500" /></div>
        ) : !report ? (
          <div className="py-8 text-center text-slate-500">Failed to load report.</div>
        ) : (
          <div className="flex-1 overflow-y-auto space-y-4">
            {/* Summary tiles */}
            <div className="grid grid-cols-3 gap-3">
              <div className="p-3 rounded-lg bg-emerald-50 border border-emerald-200">
                <div className="text-2xl font-bold text-emerald-700">{summary.sent}</div>
                <div className="text-xs text-emerald-600 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Sent</div>
              </div>
              <div className="p-3 rounded-lg bg-rose-50 border border-rose-200">
                <div className="text-2xl font-bold text-rose-700">{summary.failed}</div>
                <div className="text-xs text-rose-600 flex items-center gap-1"><XCircle className="w-3 h-3" /> Failed</div>
              </div>
              <div className="p-3 rounded-lg bg-slate-100 border border-slate-200">
                <div className="text-2xl font-bold text-slate-700">{summary.pending}</div>
                <div className="text-xs text-slate-600 flex items-center gap-1"><Clock className="w-3 h-3" /> Pending</div>
              </div>
            </div>
            <div className="text-xs text-slate-500 flex items-center gap-2">
              <span>Total recipients: <strong>{total}</strong></span>
              <span>·</span>
              <span>Success rate: <strong className={successRate >= 95 ? 'text-emerald-700' : successRate >= 80 ? 'text-amber-700' : 'text-rose-700'}>{successRate}%</strong></span>
              {successRate < 80 && total > 0 && (
                <span className="ml-auto flex items-center gap-1 text-rose-600">
                  <AlertTriangle className="w-3.5 h-3.5" /> High failure rate
                </span>
              )}
            </div>

            {/* Failure breakdown */}
            {Object.keys(report.failed_by_reason || {}).length > 0 && (
              <div className="p-3 rounded-lg bg-slate-50 border border-slate-200">
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">Failures by reason</p>
                <div className="flex flex-wrap gap-1.5">
                  {Object.entries(report.failed_by_reason).map(([reason, count]) => {
                    const label = REASON_LABELS[reason] || REASON_LABELS.unknown;
                    return (
                      <Badge key={reason} variant="outline" className={`text-xs ${label.color}`}>
                        {label.label}: {count}
                      </Badge>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Tabs + search */}
            <div className="flex flex-wrap gap-2 items-center border-b border-slate-200 pb-2">
              {['failed', 'sent', 'pending', 'all'].map((t) => (
                <button
                  key={t}
                  onClick={() => setTab(t)}
                  className={`px-2 py-1 text-xs font-medium rounded ${tab === t ? 'bg-teal-100 text-teal-700' : 'text-slate-500 hover:text-slate-700'}`}
                  data-testid={`delivery-tab-${t}`}
                >
                  {t.charAt(0).toUpperCase() + t.slice(1)} ({t === 'all' ? total : (summary[t] || 0)})
                </button>
              ))}
              <div className="relative ml-auto flex-1 min-w-[160px] max-w-[260px]">
                <Search className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
                <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search email…" className="pl-7 h-8 text-xs" data-testid="delivery-search-input" />
              </div>
            </div>

            {/* Deliveries list */}
            <div className="space-y-1" data-testid="delivery-list">
              {filtered.length === 0 ? (
                <p className="text-center text-slate-500 py-6 text-sm">No recipients match this filter.</p>
              ) : (
                filtered.map((d) => {
                  const label = REASON_LABELS[d.reason] || REASON_LABELS.unknown;
                  return (
                    <div key={d.id || d.email} className="flex items-center gap-2 p-2 hover:bg-slate-50 rounded text-xs" data-testid={`delivery-row-${d.id || d.email}`}>
                      {d.status === 'sent' && <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0" />}
                      {d.status === 'failed' && <XCircle className="w-4 h-4 text-rose-500 flex-shrink-0" />}
                      {d.status === 'pending' && <Clock className="w-4 h-4 text-slate-400 flex-shrink-0" />}
                      <span className="flex-1 truncate font-mono text-slate-700">{d.email}</span>
                      {d.status === 'failed' && (
                        <Badge variant="outline" className={`text-[10px] ${label.color}`}>{label.label}</Badge>
                      )}
                      {d.error && <span className="hidden md:inline text-slate-400 truncate max-w-[180px]" title={d.error}>{d.error}</span>}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default NewsletterDeliveryReportDialog;
